/**
 * frozt-bridge — Integration layer between Vultisig vault shares and
 * frost-zm's FROST threshold signing for Zcash Sapling.
 *
 * Architecture:
 *   Vultisig vault (.vult) ──┐
 *                             ├─► frozt KeyShareBundle ──► FroztSignSession ──► threshold signature
 *   Mobile co-signer ────────┘                              (via relay)
 *
 * frost-zm operates on the Sapling pool (RedJubjub / Jubjub), not Orchard.
 * Phrase-based wallets continue to use Orchard via PCZT.
 * Vultisig threshold wallets use Sapling via frost-zm.
 */

// Cache-busted via `?v=<build>` — see src/utils/assetCacheBust.js for
// why these public/-served WASM modules need a per-build token.
import { withCacheBust } from '../../../../utils/assetCacheBust';

const FROZT_WASM_PATH = '/frozt/frozt_wasm_bg.wasm';
const FROZT_JS_PATH = '/frozt/frozt_wasm.js';

interface FroztWasmModule {
	FroztDkgSession: any;
	FroztKeyImportSession: any;
	FroztSignSession: any;
	WasmTxBuilder: any;
	WasmShieldingTxBuilder: any;
	WasmSaplingProver: any;
	froztSignSetupMsgNew: (msg: Uint8Array, parties_data: Uint8Array) => Uint8Array;
	froztDkgSetupMsgNew: (max: number, min: number, parties: Uint8Array, birthday: bigint) => Uint8Array;
	froztKeyImportSetupMsgNew: (
		max: number, min: number, parties: Uint8Array,
		birthday: bigint, seedHolderId: number, seed: Uint8Array, accountIndex: number
	) => Uint8Array;
	frozt_sapling_derive_keys: (pubKeyPackage: Uint8Array, extras: Uint8Array) => { address: string; ivk: Uint8Array; nk: Uint8Array };
	frozt_sapling_build_dfvk: (pubKeyPackage: Uint8Array, extras: Uint8Array) => Uint8Array;
	frozt_sapling_generate_extras: () => Uint8Array;
	frozt_keyshare_bundle_pack: (kp: Uint8Array, pkp: Uint8Array, extras: Uint8Array, birthday: bigint) => Uint8Array;
	frozt_keyshare_bundle_key_package: (bundle: Uint8Array) => Uint8Array;
	frozt_keyshare_bundle_pub_key_package: (bundle: Uint8Array) => Uint8Array;
	frozt_keyshare_bundle_sapling_extras: (bundle: Uint8Array) => Uint8Array;
	frozt_keyshare_bundle_birthday: (bundle: Uint8Array) => bigint;
	frozt_encode_identifier: (id: number) => Uint8Array;
	encode_map: (entries: Array<[Uint8Array, Uint8Array]>) => Uint8Array;
}

let wasmModule: FroztWasmModule | null = null;
let initPromise: Promise<FroztWasmModule> | null = null;

export async function initFroztWasm(): Promise<FroztWasmModule> {
	if (wasmModule) return wasmModule;
	if (initPromise) return initPromise;

	initPromise = (async () => {
		try {
			const jsModule = await import(/* webpackIgnore: true */ withCacheBust(FROZT_JS_PATH));
			const wasmResponse = await fetch(withCacheBust(FROZT_WASM_PATH));
			const wasmBytes = await wasmResponse.arrayBuffer();
			await jsModule.default(wasmBytes);
			wasmModule = jsModule as unknown as FroztWasmModule;
			console.log('[frozt] WASM initialised');
			return wasmModule;
		} catch (err) {
			initPromise = null;
			throw new Error(`Failed to initialise frozt WASM: ${(err as Error).message}`);
		}
	})();

	return initPromise;
}

export function getFroztWasm(): FroztWasmModule | null {
	return wasmModule;
}

export interface FroztKeyBundle {
	keyPackage: Uint8Array;
	pubKeyPackage: Uint8Array;
	saplingExtras: Uint8Array;
	birthday: number;
	rawBundle: Uint8Array;
}

/**
 * Unpack a frozt KeyShareBundle into its components.
 */
export function unpackBundle(bundle: Uint8Array): FroztKeyBundle {
	const wasm = getFroztWasm();
	if (!wasm) throw new Error('frozt WASM not initialised');
	return {
		keyPackage: wasm.frozt_keyshare_bundle_key_package(bundle),
		pubKeyPackage: wasm.frozt_keyshare_bundle_pub_key_package(bundle),
		saplingExtras: wasm.frozt_keyshare_bundle_sapling_extras(bundle),
		birthday: Number(wasm.frozt_keyshare_bundle_birthday(bundle)),
		rawBundle: bundle,
	};
}

/**
 * Derive the Sapling z-address from a key share bundle.
 */
export function deriveSaplingAddress(bundle: FroztKeyBundle): string {
	const wasm = getFroztWasm();
	if (!wasm) throw new Error('frozt WASM not initialised');
	const keys = wasm.frozt_sapling_derive_keys(bundle.pubKeyPackage, bundle.saplingExtras);
	return keys.address;
}

/**
 * Derive the Sapling DFVK (Diversifiable Full Viewing Key) for scanning.
 */
export function deriveSaplingDfvk(bundle: FroztKeyBundle): Uint8Array {
	const wasm = getFroztWasm();
	if (!wasm) throw new Error('frozt WASM not initialised');
	return wasm.frozt_sapling_build_dfvk(bundle.pubKeyPackage, bundle.saplingExtras);
}

export interface SignSessionState {
	session: any;
	partyName: string;
	done: boolean;
}

/**
 * Encode party identifiers for the FROST ceremony.
 * Parties are 1-indexed and sorted by name.
 */
export function encodeParties(partyNames: string[]): Uint8Array {
	const wasm = getFroztWasm();
	if (!wasm) throw new Error('frozt WASM not initialised');

	const sorted = [...partyNames].sort();
	const entries: Array<[Uint8Array, Uint8Array]> = sorted.map((name, i) => {
		const nameBytes = new TextEncoder().encode(name);
		const idBytes = wasm.frozt_encode_identifier(i + 1);
		return [nameBytes, idBytes];
	});
	return wasm.encode_map(entries);
}

/**
 * Start a FROST signing session.
 *
 * @param msgToSign — The sighash to sign (32 bytes)
 * @param partyNames — All participating party names (sorted)
 * @param myPartyName — This device's party name
 * @param keyBundle — This device's key share bundle
 * @param alpha — Optional rerandomization value (from WasmTxBuilder.alpha)
 */
export function startSignSession(
	msgToSign: Uint8Array,
	partyNames: string[],
	myPartyName: string,
	keyBundle: FroztKeyBundle,
	alpha?: Uint8Array,
): SignSessionState {
	const wasm = getFroztWasm();
	if (!wasm) throw new Error('frozt WASM not initialised');

	const partiesData = encodeParties(partyNames);
	const setupMsg = wasm.froztSignSetupMsgNew(msgToSign, partiesData);

	const session = alpha
		? wasm.FroztSignSession.fromSetupWithAlpha(
			setupMsg, myPartyName, keyBundle.keyPackage, keyBundle.pubKeyPackage, alpha
		)
		: wasm.FroztSignSession.fromSetup(
			setupMsg, myPartyName, keyBundle.keyPackage, keyBundle.pubKeyPackage
		);

	return { session, partyName: myPartyName, done: false };
}

/**
 * Drive one round of the signing message loop.
 * Returns outbound messages to send to other parties.
 */
export function signSessionTakeMessages(state: SignSessionState): Array<{ recipient: string; msg: Uint8Array }> {
	const outbound: Array<{ recipient: string; msg: Uint8Array }> = [];
	let msg = state.session.takeMsg();
	while (msg) {
		let idx = 0;
		let receiver = state.session.msgReceiver(msg, idx);
		while (receiver !== undefined) {
			outbound.push({ recipient: receiver, msg });
			idx++;
			receiver = state.session.msgReceiver(msg, idx);
		}
		msg = state.session.takeMsg();
	}
	return outbound;
}

/**
 * Feed an inbound message from another party.
 * Returns true when the signing ceremony is complete.
 */
export function signSessionFeed(state: SignSessionState, inboundMsg: Uint8Array): boolean {
	const done = state.session.feed(inboundMsg);
	state.done = done;
	return done;
}

/**
 * Extract the threshold signature (only valid after feed() returns true).
 */
export function signSessionResult(state: SignSessionState): Uint8Array {
	if (!state.done) throw new Error('Signing session not complete');
	return state.session.result();
}

/**
 * Start a DKG session for generating new Sapling key shares.
 */
export function startDkgSession(
	maxSigners: number,
	minSigners: number,
	partyNames: string[],
	myPartyName: string,
	birthday: number,
): { session: any; partyName: string; done: boolean } {
	const wasm = getFroztWasm();
	if (!wasm) throw new Error('frozt WASM not initialised');

	const partiesData = encodeParties(partyNames);
	const setupMsg = wasm.froztDkgSetupMsgNew(maxSigners, minSigners, partiesData, BigInt(birthday));
	const session = wasm.FroztDkgSession.fromSetup(setupMsg, myPartyName, BigInt(birthday));
	return { session, partyName: myPartyName, done: false };
}

/**
 * Start a Key Import session to split an existing Zcash spending key
 * into threshold shares compatible with the existing vault structure.
 */
export function startKeyImportSession(
	maxSigners: number,
	minSigners: number,
	partyNames: string[],
	myPartyName: string,
	birthday: number,
	seedHolderId: number,
	seed: Uint8Array,
	accountIndex: number,
): { session: any; partyName: string; done: boolean } {
	const wasm = getFroztWasm();
	if (!wasm) throw new Error('frozt WASM not initialised');

	const partiesData = encodeParties(partyNames);
	const setupMsg = wasm.froztKeyImportSetupMsgNew(
		maxSigners, minSigners, partiesData,
		BigInt(birthday), seedHolderId, seed, accountIndex,
	);
	const session = wasm.FroztKeyImportSession.fromSetup(
		setupMsg, myPartyName, seed, accountIndex, BigInt(birthday),
	);
	return { session, partyName: myPartyName, done: false };
}

/**
 * Check whether frozt WASM is available and ready.
 */
export function isFroztReady(): boolean {
	return wasmModule !== null;
}
